import { useEffect } from 'react';

import { useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { useNavigate } from 'react-router';
import { useAccountSelector } from 'src/hooks';
import { MAlert } from 'src/lib';
import { ScreenBreakpoint } from 'src/styles';
import {
  isAccountDisclosureComplete,
  shouldDisplayAccountHolderInformation,
  shouldDisplayJointAccountHolderInformation,
} from 'src/utils';

import { MyAccountLayout } from '../../../lib/Layout/MyAccountLayout/MyAccountLayout';
import { MyAccountSidebarMainMenuItemKey } from '../../../lib/Layout/MyAccountLayout/MyAccountSidebar';

import { AccountHolderDisclosureSection } from './AccountHolderDisclosureSection';
import { JointAccountHolderDisclosureSection } from './JointAccountHolderDisclosureSection';
import { OtherAccountHolderListDisclosureSection } from './OtherAccountHolderListDisclosureSection';

export const Disclosures = () => {
  const navigate = useNavigate();

  const isCreateSuccess = useSelector((state: any) => Boolean(state.accountHolders.createDisclosures.__succeeded));
  const isPatchSuccess = useSelector((state: any) => Boolean(state.accountHolders.patchDisclosures.__succeeded));

  const { account } = useAccountSelector();

  const isMobile = useMediaQuery({ query: `(max-width: ${ScreenBreakpoint.mobile.max})` });

  const hasAnyUpsertRequest = () => isCreateSuccess || isPatchSuccess;

  const shouldSendAnalyticsEvent = () => !account?.accountId && isCreateSuccess && isAccountDisclosureComplete(account);

  const shouldRedirectToAccountAgreements = () =>
    !account?.accountId && hasAnyUpsertRequest() && isAccountDisclosureComplete(account);

  const shouldDisplayOtherAccountHolderListSection = () => {
    return (
      account?.type.isEntity &&
      account?.isEntityAccountTypeActive &&
      account?.otherAccountHolders &&
      account?.otherAccountHolders?.length > 0
    );
  };

  useEffect(() => {
    if (shouldSendAnalyticsEvent()) {
      window.gtag('event', 'account_disclosures_complete');
    }
  }, [isCreateSuccess]);

  useEffect(() => {
    if (shouldRedirectToAccountAgreements()) {
      navigate(`/accounts/account-agreements`);
    }
  }, [isCreateSuccess, isPatchSuccess]);

  return (
    <MyAccountLayout
      sidebarMenuItemKey={MyAccountSidebarMainMenuItemKey.Disclosures}
      title={
        isMobile
          ? undefined
          : account?.type.isJoint || account?.type.isEntity || account?.type.isUgma
          ? 'Primary Account Holder'
          : 'Disclosures'
      }>
      {account?.lastFailedLinkAttemptError && (
        <MAlert
          type='error'
          description={`You cannot edit account information until your ${account.customTypeLabel} account has been successfully linked.`}
        />
      )}
      {shouldDisplayAccountHolderInformation({
        type: account?.type,
        isEntityAccountTypeActive: account?.isEntityAccountTypeActive,
      }) && <AccountHolderDisclosureSection />}
      {shouldDisplayJointAccountHolderInformation({
        type: account?.type,
        isEntityAccountTypeActive: account?.isEntityAccountTypeActive,
      }) && <JointAccountHolderDisclosureSection />}
      {shouldDisplayOtherAccountHolderListSection() && <OtherAccountHolderListDisclosureSection />}
    </MyAccountLayout>
  );
};
